import { ContentfulCloudinaryImageType } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { Critic } from '@/types/codegen-federation'
import {
  mapTheatricalReleaseCriticToRating,
  TheatricalRatingData,
} from '@/utils/theatrical-ratings/theatrical-ratings-utils'

// Flexible theatrical release interface that accepts what the API actually returns
export interface TheatricalReleaseInput {
  critics?: (Critic | null)[] | null
}

// Simplified rating input that matches what Contentful provides
export interface RatingInput {
  icon?: unknown
  rating?: string | null
  author?: string | null
}

export interface ContentfulRatings {
  rottenTomatoes?: RatingInput | null
  audience?: RatingInput | null
  cinema?: RatingInput | null
  imdb?: RatingInput | null
}

export type RatingData = RatingInput | TheatricalRatingData | null | undefined

export interface NormalizedRating {
  icon?: ContentfulCloudinaryImageType | ContentfulCloudinaryImageType[]
  rating: string | null | undefined
  author: string | null | undefined
}

export interface TheatricalRatings {
  rottenTomatoes: NormalizedRating | null
  audience: NormalizedRating | null
  cinema: NormalizedRating | null
  imdb: NormalizedRating | null
  shouldShowRottenTomatoes: boolean
  shouldShowAudience: boolean
  shouldShowCinema: boolean
  shouldShowImdb: boolean
}

/**
 * Custom hook to extract and process theatrical ratings from both Contentful and theatrical release data.
 * Provides fallback logic: uses Contentful data first, falls back to theatrical release data.
 *
 * @param theatricalRelease - Theatrical release object containing critics and scores (can be partial)
 * @param contentfulRatings - Optional ratings from Contentful CMS
 * @returns Processed ratings with display conditions
 */
export const useTheatricalRatings = (
  theatricalRelease?: TheatricalReleaseInput | null,
  contentfulRatings?: ContentfulRatings,
): TheatricalRatings => {
  // Extract critics from theatrical release
  const rottenTomatoesCritic = theatricalRelease?.critics?.find(
    (critic: Critic | null) => critic?.company?.toLowerCase() === 'rotten tomatoes',
  )
  const imdbCritic = theatricalRelease?.critics?.find(
    (critic: Critic | null) => critic?.company?.toLowerCase() === 'imdb',
  )

  // Extract specific scores
  const theatricalReleaseTomatometer = rottenTomatoesCritic?.scores?.find(
    (score) => score?.name?.toLowerCase() === 'tomatometer',
  )
  const theatricalReleaseAudience = rottenTomatoesCritic?.scores?.find(
    (score) => score?.name?.toLowerCase() === 'audience',
  )
  const theatricalReleaseImdb = imdbCritic?.scores?.find((score) => score?.name?.toLowerCase() === 'rating')

  // Extract special flags
  const certifiedFresh = Boolean(rottenTomatoesCritic?.certifiedFresh)
  const verifiedHot = Boolean(rottenTomatoesCritic?.verifiedHot)

  // Helper function to normalize rating data from either source
  const normalizeRating = (rating: RatingData): NormalizedRating | null => {
    if (!rating) return null
    return {
      icon: rating.icon as ContentfulCloudinaryImageType | ContentfulCloudinaryImageType[] | undefined,
      rating: rating.rating,
      author: rating.author,
    }
  }

  // Build ratings with fallback logic (Contentful first, theatrical release second)
  const rottenTomatoesRaw =
    contentfulRatings?.rottenTomatoes ||
    (theatricalReleaseTomatometer &&
      mapTheatricalReleaseCriticToRating(theatricalReleaseTomatometer, certifiedFresh, verifiedHot))

  const audienceRaw =
    contentfulRatings?.audience ||
    (theatricalReleaseAudience &&
      mapTheatricalReleaseCriticToRating(theatricalReleaseAudience, certifiedFresh, verifiedHot))

  const cinemaRaw = contentfulRatings?.cinema

  const imdbRaw =
    contentfulRatings?.imdb ||
    (theatricalReleaseImdb && mapTheatricalReleaseCriticToRating(theatricalReleaseImdb, certifiedFresh, verifiedHot))

  // Normalize all ratings
  const rottenTomatoes = normalizeRating(rottenTomatoesRaw)
  const audience = normalizeRating(audienceRaw)
  const cinema = normalizeRating(cinemaRaw)
  const imdb = normalizeRating(imdbRaw)

  // Calculate display conditions
  const shouldShowRottenTomatoes = Boolean(
    rottenTomatoes && rottenTomatoes.rating && parseInt(rottenTomatoes.rating) >= 70,
  )
  const shouldShowAudience = Boolean(audience && audience.rating && parseInt(audience.rating) >= 70)
  const shouldShowCinema = Boolean(cinema)
  const shouldShowImdb = Boolean(imdb && imdb.rating && parseFloat(imdb.rating) >= 7)

  return {
    rottenTomatoes,
    audience,
    cinema,
    imdb,
    shouldShowRottenTomatoes,
    shouldShowAudience,
    shouldShowCinema,
    shouldShowImdb,
  }
}
